import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, isRef as _isRef, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { ref, computed, watch, nextTick, provide } from "vue";
import BaseModal from "../BaseModal.vue";
import BookingForm from "../BookingForm.vue";
import BookingDetailsView from "../BookingDetailsView.vue";
import SeriesActionChoiceDialog from "../SeriesActionChoiceDialog.vue";
import BookingBudgetErrorPanel from "./BookingBudgetErrorPanel.vue";
import BookingComplianceErrorPanel from "./BookingComplianceErrorPanel.vue";
import BookingConflictResolutionPanel from "./BookingConflictResolutionPanel.vue";
import BookingErrorActions from "./BookingErrorActions.vue";
import BookingConflictActions from "./BookingConflictActions.vue";
import BookingViewActions from "./BookingViewActions.vue";
import BookingFormActions from "./BookingFormActions.vue";
import {
	bookingFormContextKey,
	createBookingFormContext,
} from "./bookingFormContext.js";
import { useConflictResolution } from "../../composables/useConflictResolution.js";


export default {
  __name: 'BookingModal',
  props: {
	show: { type: Boolean, default: false },
	mode: {
		type: String,
		default: "create",
	}, // 'create' | 'edit' | 'view' | 'conflict' | 'budget-error' | 'compliance-error' | ...
	booking: { type: Object, default: null },
	selectedSlot: { type: Object, default: null },
	prefillData: { type: Object, default: null },
	isLoading: { type: Boolean, default: false },
	conflictData: { type: Object, default: () => ({ hasConflict: false }) },
	budgetError: { type: Object, default: null },
	complianceError: { type: Object, default: null },
	isDragDrop: { type: Boolean, default: false },
	isWalkIn: { type: Boolean, default: false },
},
  emits: [
	"close",
	"save",
	"save-walkin",
	"update",
	"update-series",
	"update-instance",
	"delete",
	"end-walkin",
	"switch-to-edit",
	"resolve-conflict",
	"cancel-conflict",
	"edit-series",
	"edit-instance",
	"delete-series",
	"delete-instance",
	"resolve-budget-error",
	"resolve-compliance-error",
],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const formContext = createBookingFormContext();
provide(bookingFormContextKey, formContext);

const bookingFormRef = ref(null);

const {
	selectedResolution,
	isSeriesBooking,
	resetDefaultResolution,
	resolve: resolveConflict,
	cancel: cancelConflictSelection,
} = useConflictResolution(() => props.conflictData);

const seriesChoiceState = ref({
	show: false,
	action: "",
	booking: null,
});

const isFormMode = computed(
	() =>
		props.mode !== "view" &&
		props.mode !== "conflict" &&
		props.mode !== "budget-error" &&
		props.mode !== "compliance-error"
);

const hasBookingSeries = computed(
	() => !!(props.booking?.series && props.booking.series.id)
);

const modalTitle = computed(() => {
	if (props.mode === "conflict") return "Terminkonflikt erkannt";
	if (props.mode === "budget-error" || props.mode === "compliance-error") {
		return "Buchung nicht möglich";
	}
	if (props.mode === "view") return "Buchung";
	if (props.isWalkIn && isFormMode.value) return "Sofortbuchung";
	return formContext.modalTitle || "Buchung";
});

function handleConflictResolve() {
	const payload = resolveConflict();
	if (payload) {
		emit("resolve-conflict", payload);
	}
}

function handleConflictCancel() {
	cancelConflictSelection();
	emit("cancel-conflict");
}

watch(
	() => props.show,
	(show) => {
		if (!show) return;

		resetDefaultResolution();

		nextTick(() => {
			bookingFormRef.value?.focusFirstInput?.();
		});
	}
);

function handleEditClick() {
	if (hasBookingSeries.value) {
		seriesChoiceState.value = {
			show: true,
			action: "edit",
			booking: props.booking,
		};
		return;
	}
	emit("switch-to-edit", props.booking);
}

function handleDeleteClick() {
	if (hasBookingSeries.value) {
		seriesChoiceState.value = {
			show: true,
			action: "delete",
			booking: props.booking,
		};
		return;
	}
	emit("delete", props.booking?.id);
}

function closeSeriesChoiceDialog() {
	seriesChoiceState.value = {
		show: false,
		action: "",
		booking: null,
	};
}

function handleSeriesActionChoice(choice) {
	const { action, scope, booking } = choice;
	closeSeriesChoiceDialog();

	if (action === "edit") {
		emit(scope === "series" ? "edit-series" : "edit-instance", booking);
		return;
	}

	if (action === "delete") {
		if (scope === "series") {
			emit("delete-series", booking.series.id);
		} else {
			emit("delete-instance", booking.id);
		}
	}
}

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(BaseModal, {
      show: __props.show,
      title: modalTitle.value,
      size: "lg",
      "modal-id": "bookingModal",
      "is-loading": __props.isLoading,
      onClose: _cache[14] || (_cache[14] = $event => (_ctx.$emit('close')))
    }, {
      body: _withCtx(() => [
        (__props.mode === 'view')
          ? (_openBlock(), _createBlock(BookingDetailsView, {
              key: 0,
              booking: __props.booking
            }, null, 8 /* PROPS */, ["booking"]))
          : (isFormMode.value && __props.show)
            ? (_openBlock(), _createBlock(BookingForm, {
                key: 1,
                ref_key: "bookingFormRef",
                ref: bookingFormRef,
                show: __props.show,
                mode: __props.mode,
                booking: __props.booking,
                "selected-slot": __props.selectedSlot,
                "prefill-data": __props.prefillData,
                "is-drag-drop": __props.isDragDrop,
                "is-walk-in": __props.isWalkIn,
                onSave: _cache[0] || (_cache[0] = $event => (_ctx.$emit('save', $event))),
                onSaveWalkin: _cache[1] || (_cache[1] = $event => (_ctx.$emit('save-walkin', $event))),
                onUpdate: _cache[2] || (_cache[2] = 
          (bookingId, bookingData) => _ctx.$emit('update', bookingId, bookingData)
        ),
                onUpdateSeries: _cache[3] || (_cache[3] = 
          (seriesId, bookingData) =>
            _ctx.$emit('update-series', seriesId, bookingData)
        ),
                onUpdateInstance: _cache[4] || (_cache[4] = 
          (bookingId, bookingData) =>
            _ctx.$emit('update-instance', bookingId, bookingData)
        )
              }, null, 8 /* PROPS */, ["show", "mode", "booking", "selected-slot", "prefill-data", "is-drag-drop", "is-walk-in"]))
            : (__props.mode === 'budget-error')
              ? (_openBlock(), _createBlock(BookingBudgetErrorPanel, {
                  key: 2,
                  "budget-error": __props.budgetError
                }, null, 8 /* PROPS */, ["budget-error"]))
              : (__props.mode === 'compliance-error')
                ? (_openBlock(), _createBlock(BookingComplianceErrorPanel, {
                    key: 3,
                    "compliance-error": __props.complianceError
                  }, null, 8 /* PROPS */, ["compliance-error"]))
                : (__props.mode === 'conflict')
                  ? (_openBlock(), _createBlock(BookingConflictResolutionPanel, {
                      key: 4,
                      "selected-resolution": _unref(selectedResolution),
                      "onUpdate:selectedResolution": _cache[5] || (_cache[5] = $event => (_isRef(selectedResolution) ? (selectedResolution).value = $event : null)),
                      "conflict-data": __props.conflictData
                    }, null, 8 /* PROPS */, ["selected-resolution", "conflict-data"]))
                  : _createCommentVNode("v-if", true)
      ]),
      footer: _withCtx(() => [
        (__props.mode === 'budget-error')
          ? (_openBlock(), _createBlock(BookingErrorActions, {
              key: 0,
              "is-loading": __props.isLoading,
              onClose: _cache[6] || (_cache[6] = $event => (_ctx.$emit('close'))),
              onResolve: _cache[7] || (_cache[7] = $event => (_ctx.$emit('resolve-budget-error')))
            }, null, 8 /* PROPS */, ["is-loading"]))
          : (__props.mode === 'compliance-error')
            ? (_openBlock(), _createBlock(BookingErrorActions, {
                key: 1,
                "is-loading": __props.isLoading,
                onClose: _cache[8] || (_cache[8] = $event => (_ctx.$emit('close'))),
                onResolve: _cache[9] || (_cache[9] = $event => (_ctx.$emit('resolve-compliance-error')))
              }, null, 8 /* PROPS */, ["is-loading"]))
            : (__props.mode === 'conflict')
              ? (_openBlock(), _createBlock(BookingConflictActions, {
                  key: 2,
                  "selected-resolution": _unref(selectedResolution),
                  "is-loading": __props.isLoading,
                  "is-series-booking": _unref(isSeriesBooking),
                  onCancel: handleConflictCancel,
                  onBack: _cache[10] || (_cache[10] = $event => (_ctx.$emit('cancel-conflict'))),
                  onResolve: handleConflictResolve
                }, null, 8 /* PROPS */, ["selected-resolution", "is-loading", "is-series-booking"]))
              : (__props.mode === 'view')
                ? (_openBlock(), _createBlock(BookingViewActions, {
                    key: 3,
                    booking: __props.booking,
                    show: __props.show,
                    "is-loading": __props.isLoading,
                    onClose: _cache[11] || (_cache[11] = $event => (_ctx.$emit('close'))),
                    onEdit: handleEditClick,
                    onDelete: handleDeleteClick,
                    onEndWalkin: _cache[12] || (_cache[12] = $event => (_ctx.$emit('end-walkin', __props.booking?.id)))
                  }, null, 8 /* PROPS */, ["booking", "show", "is-loading"]))
                : (isFormMode.value && __props.show)
                  ? (_openBlock(), _createBlock(BookingFormActions, {
                      key: 4,
                      booking: __props.booking,
                      show: __props.show,
                      "is-loading": __props.isLoading,
                      onClose: _cache[13] || (_cache[13] = $event => (_ctx.$emit('close')))
                    }, null, 8 /* PROPS */, ["booking", "show", "is-loading"]))
                  : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["show", "title", "is-loading"]),
    _createVNode(SeriesActionChoiceDialog, {
      show: seriesChoiceState.value.show,
      action: seriesChoiceState.value.action,
      booking: seriesChoiceState.value.booking,
      onClose: closeSeriesChoiceDialog,
      onConfirm: handleSeriesActionChoice
    }, null, 8 /* PROPS */, ["show", "action", "booking"])
  ], 64 /* STABLE_FRAGMENT */))
}
}

}